import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PromoBar } from "@/components/PromoBar";
import { isFreePeriod } from "@/lib/pricing";
import { useSeo } from "@/lib/seo";
import { SeoJsonLd } from "@/components/SeoJsonLd";
import { HardHat, ArrowRight, CheckCircle2, Building2, Wrench, ClipboardList, Crown, Gift, Users, Home } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBidWlsZGluZyUyMHdpbmRvd3MlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzgxNzI3MTgzfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const free = isFreePeriod();
  useSeo({
    title: "Work4you — Vakmensen voor ramen & deuren | Professionals, bedrijven & particulieren",
    description: "Hét platform voor ramen- en deurenmontage in België en Nederland. Professionals vinden opdrachten, bedrijven plaatsen klussen en particulieren vragen gratis offertes aan bij geverifieerde vakmensen. PVC, aluminium en houten ramen, schuiframen en voordeuren.",
    path: "/",
    keywords: "PVC ramen, aluminium ramen, houten ramen, schuiframen, ramen monteren, raamkozijnen monteren, voordeuren, raammonteurs, kozijnenmonteurs, nieuwe ramen en deuren, woning renovatie, raam- en deurkozijnen, ramen en deuren België, ramen en deuren Nederland, Kömmerling, Schüco, Aliplast, Aluplast, Deceuninck, Veka, Meranti, Aluprof",
  });
  return (
    <div className="min-h-screen bg-slate-50">
      <SeoJsonLd />
      {/* Sticky top: promo bar + nav */}
      <div className="sticky top-0 z-50">
        <PromoBar to="/registreren" />
        <nav className="backdrop-blur-xl bg-white/90 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-500 flex items-center justify-center rounded-sm">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-display text-lg font-extrabold tracking-tight">Work4you</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button data-testid="nav-home-btn" variant="ghost" className="rounded-sm font-semibold gap-2 text-slate-700 hover:text-orange-600">
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">Home</span>
                </Button>
              </Link>
              <Link to="/opdrachten">
                <Button data-testid="nav-opdrachten-btn" variant="ghost" className="rounded-sm font-semibold gap-2 text-slate-700 hover:text-blue-700">
                  <ClipboardList className="w-4 h-4" />
                  <span className="hidden sm:inline">Opdrachten</span>
                </Button>
              </Link>
              <Link to="/beschikbare_monteurs">
                <Button data-testid="nav-monteurs-btn" variant="ghost" className="rounded-sm font-semibold gap-2 text-slate-700 hover:text-emerald-700">
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Professionals</span>
                </Button>
              </Link>
              <Link to="/inloggen">
                <Button data-testid="nav-login-btn" variant="ghost" className="rounded-sm font-semibold">
                  Inloggen
                </Button>
              </Link>
              <Link to="/registreren">
                <Button data-testid="nav-register-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-semibold">
                  Aanmelden
                </Button>
              </Link>
            </div>
          </div>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Bouw" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <div className="max-w-3xl">
            <p className="label-caps text-orange-400 mb-4">Ramen & Deuren Montage</p>
            <span data-testid="intro-deal-badge" className="relative inline-flex items-center gap-2 bg-orange-500 text-white rounded-full pl-2 pr-4 py-2 mb-6 text-sm font-bold shadow-lg shadow-orange-900/40">
              <span className="absolute inset-0 rounded-full ring-2 ring-orange-400/60 animate-ping opacity-40" />
              {free ? (
                <>
                  <span className="inline-flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5"><Gift className="w-3.5 h-3.5" /> Gratis</span>
                  Gratis voor iedereen tot 31 december 2026
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5"><Crown className="w-3.5 h-3.5" /> Pro</span>
                  Probeer je eerste kwartaal voor maar €1
                </>
              )}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] mb-6">
              Het platform dat <span className="text-orange-500">professionals</span> koppelt aan bedrijven — én waar je een prijsaanvraag doet
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              Stel je beschikbaar als zelfstandig professional, vind in enkele klikken de juiste vakman, of vraag een prijsofferte aan voor nieuwe ramen en/of deuren, garagepoorten, rolluiken en/of screens en Velux (dakramen).
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/registreren?rol=monteur">
                <Button data-testid="hero-monteur-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold px-8 h-12 text-base w-full sm:w-auto">
                  Ik ben professional <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/registreren?rol=bedrijf">
                <Button data-testid="hero-bedrijf-btn" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-slate-950 rounded-sm font-bold px-8 h-12 text-base w-full sm:w-auto">
                  Ik zoek professionals
                </Button>
              </Link>
            </div>
            <Link to="/offerte-aanvragen" data-testid="hero-particulier-link"
              className="group mt-8 inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 transition-colors rounded-md px-6 py-4 shadow-lg shadow-orange-900/30">
              <span className="text-lg sm:text-2xl font-display font-extrabold text-white">
                Particulier? Vraag hier een prijsofferte aan voor ramen, deuren, garagepoorten, rolluiken/screens en Velux
              </span>
              <ArrowRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <p className="label-caps mb-3">Hoe het werkt</p>
        <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-950 mb-12 max-w-2xl">
          Van aanmelding tot opdracht in drie stappen
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Wrench, t: "Stel je beschikbaar", d: "Professionals maken een profiel aan met specialisatie, ervaring en uurtarief en geven hun beschikbaarheid aan." },
            { icon: ClipboardList, t: "Plaats of vind opdrachten", d: "Bedrijven plaatsen opdrachten op het bord of zoeken gericht in de professionalsgids en sturen een aanvraag." },
            { icon: CheckCircle2, t: "Aanvaard in je backoffice", d: "Elke professional beheert opdrachten en aanvragen in een eigen backoffice en accepteert met één klik." },
          ].map((s, i) => (
            <div key={s.t} className="bg-white border border-slate-200 rounded-md p-8 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
              <div className="w-12 h-12 bg-slate-950 flex items-center justify-center rounded-sm mb-5">
                <s.icon className="w-6 h-6 text-orange-500" />
              </div>
              <span className="font-display text-4xl font-black text-slate-200">0{i + 1}</span>
              <h3 className="font-display text-xl font-bold text-slate-950 mt-2 mb-2">{s.t}</h3>
              <p className="text-slate-500 leading-relaxed text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Two audiences */}
      <section className="bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-6">
          {[
            { icon: Wrench, t: "Voor professionals", pts: ["Eigen backoffice & profiel", "Reageer op opdrachten", "Bepaal je eigen uurtarief", free ? "Volledig gratis tot 31 dec 2026" : "Pro €39/kwartaal"], cta: "Word professional", rol: "monteur" },
            { icon: Building2, t: "Voor bedrijven", pts: ["Doorzoek de professionalsgids gratis", "Plaats opdrachten & stuur aanvragen", "Geverifieerd-badge via VIES", free ? "Volledig gratis tot 31 dec 2026" : "Pro €39/kwartaal"], cta: "Vind professionals", rol: "bedrijf" },
          ].map((c) => (
            <div key={c.t} className="border border-slate-800 rounded-md p-8">
              <c.icon className="w-8 h-8 text-orange-500 mb-4" />
              <h3 className="font-display text-2xl font-bold text-white mb-4">{c.t}</h3>
              <ul className="space-y-2 mb-6">
                {c.pts.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <Link to={`/registreren?rol=${c.rol}`}>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
                  {c.cta} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Prijzen */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <p className="label-caps mb-3">Transparante prijzen</p>
        <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-950 mb-4 max-w-2xl">
          Heldere tarieven, geen verborgen kosten
        </h2>
        <p className="text-slate-500 mb-12 max-w-xl">
          {free
            ? "Alles is volledig gratis voor iedereen tot 31 december 2026. Daarna schakelt Pro alle functies vrij voor €39/kwartaal."
            : "Start met een gratis profiel. Pro schakelt alle functies vrij voor €39 per kwartaal."}
        </p>
        {free ? (
          <div data-testid="free-pricing-block" className="bg-slate-950 text-white rounded-md p-10 lg:p-14 text-center">
            <span className="inline-flex items-center gap-2 bg-green-500/20 text-green-300 rounded-full px-4 py-1.5 text-sm font-bold mb-5">
              <Gift className="w-4 h-4" /> Tijdelijke actie
            </span>
            <h3 className="font-display text-3xl lg:text-4xl font-black mb-3">Gratis voor iedereen tot 31 december 2026</h3>
            <p className="text-slate-300 max-w-2xl mx-auto mb-8">
              Alle functies zijn volledig gratis: professionals reageren op opdrachten, bedrijven plaatsen opdrachten en sturen aanvragen, en particulieren vragen gratis offertes aan. Geen abonnement, geen verborgen kosten.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/registreren?rol=monteur"><Button data-testid="free-cta-monteur" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-11 px-6">Word professional <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/registreren?rol=bedrijf"><Button data-testid="free-cta-bedrijf" className="bg-white text-slate-950 hover:bg-slate-100 rounded-sm font-bold h-11 px-6">Word bedrijf</Button></Link>
              <Link to="/offerte-aanvragen"><Button data-testid="free-cta-particulier" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-950 rounded-sm font-bold h-11 px-6">Vraag een offerte aan</Button></Link>
            </div>
          </div>
        ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              naam: "Professional Pro", prijs: "€39", periode: "/kwartaal", intro: "Per kwartaal",
              pts: ["Reageer op alle opdrachten", "Bovenaan in de professionalsgids", "Pro-badge op je profiel", "Eigen backoffice & agenda"],
              cta: "Word professional", rol: "monteur", highlight: false,
            },
            {
              naam: "Bedrijf Pro", prijs: "€39", periode: "/kwartaal", intro: "Per kwartaal",
              pts: ["Plaats onbeperkt opdrachten", "Stuur directe aanvragen", "Geverifieerd-badge via VIES", "Privé-chat met professionals"],
              cta: "Word bedrijf", rol: "bedrijf", highlight: true,
            },
            {
              naam: "Particulier", prijs: "€3", periode: "/opdracht", intro: "Geen abonnement",
              pts: ["Betaal alleen per opdracht", "Direct zichtbaar voor professionals", "Reacties van meerdere vakmannen", "Privé-chat per opdracht"],
              cta: "Plaats een opdracht", rol: "particulier", highlight: false,
            },
          ].map((p) => (
            <div key={p.naam} data-testid={`price-card-${p.rol}`}
              className={`relative rounded-md p-8 flex flex-col transition-all duration-200 hover:-translate-y-1 ${p.highlight ? "bg-slate-950 text-white border-2 border-orange-500 shadow-xl" : "bg-white text-slate-950 border border-slate-200 hover:shadow-md"}`}>
              {p.highlight && (
                <span className="absolute -top-3 left-8 bg-orange-500 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-sm">Populair</span>
              )}
              <h3 className="font-display text-xl font-bold mb-1">{p.naam}</h3>
              <div className="flex items-end gap-1 mb-1">
                <span className="font-display text-4xl font-black">{p.prijs}</span>
                <span className={`text-sm font-semibold mb-1 ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>{p.periode}</span>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-500 mb-6">
                <Crown className="w-3.5 h-3.5" /> {p.intro}
              </span>
              <ul className="space-y-2.5 mb-8 flex-1">
                {p.pts.map((pt) => (
                  <li key={pt} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>
                    <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" /> {pt}
                  </li>
                ))}
              </ul>
              <Link to={`/registreren?rol=${p.rol}`}>
                <Button data-testid={`price-cta-${p.rol}`}
                  className={`w-full rounded-sm font-bold h-11 ${p.highlight ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-slate-950 hover:bg-orange-500 text-white"}`}>
                  {p.cta} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-slate-950">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <p className="label-caps text-orange-400 mb-3">Veelgestelde vragen</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mb-12">
            Alles wat je wil weten
          </h2>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {(free
              ? [{ q: "Is het echt helemaal gratis?", a: "Ja! Tot 31 december 2026 is Work4you volledig gratis voor professionals, bedrijven én particulieren — geen abonnement en geen kosten per offerteaanvraag. Daarna wordt Pro €39 per kwartaal en een offerteaanvraag €3." }]
              : [{ q: "Hoe werkt het Pro-abonnement?", a: "Pro kost €39 per kwartaal en verlengt niet automatisch — je betaalt telkens zelf opnieuw wanneer je wil verlengen." }]
            ).concat([
              { q: "Kan ik op elk moment stoppen?", a: "Ja. Pro loopt per kwartaal en verlengt niet automatisch — je betaalt telkens zelf opnieuw wanneer je wil verlengen. Stop je, dan blijft je gratis profiel gewoon zichtbaar." },
              { q: "Wat krijg ik met Pro als professional?", a: "Met Pro kun je op alle opdrachten reageren, sta je bovenaan in de professionalsgids en krijg je een Pro-badge op je profiel. Zonder Pro kun je opdrachten wel bekijken." },
              { q: "Wat krijg ik met Pro als bedrijf?", a: "Met Pro plaats je onbeperkt opdrachten, stuur je directe aanvragen naar professionals en chat je privé per opdracht. De professionalsgids doorzoeken kan altijd gratis." },
              { q: "Hoe werkt de BTW-verificatie?", a: "Bij registratie controleren we je BTW-nummer automatisch via de officiële Europese VIES-database. Is het geldig, dan vullen we je bedrijfsgegevens in en krijg je een 'Geverifieerd'-badge die professionals vertrouwen geeft." },
              { q: "Wat kost het voor particulieren?", a: free ? "Tot 31 december 2026 is een offerteaanvraag volledig gratis. Daarna betaal je €3 per aanvraag; na betaling worden de gekozen bedrijven meteen verwittigd." : "Particulieren betalen geen abonnement, maar €3 per offerteaanvraag. Na betaling worden de gekozen bedrijven meteen verwittigd." },
            ]).map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-slate-800">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-white font-display font-bold text-left hover:text-orange-400 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SEO-content: diensten, materialen & merken */}
      <section className="bg-white border-t border-slate-200 py-16" data-testid="seo-content-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-950 mb-4">
            Ramen en deuren monteren in België en Nederland
          </h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            Work4you brengt vraag en aanbod samen voor de montage van <strong>PVC ramen</strong>,
            <strong> aluminium ramen</strong>, <strong>houten ramen</strong>, <strong>schuiframen</strong> en
            <strong> voordeuren</strong>. Of je nu een ervaren <strong>raammonteur</strong> of
            <strong> kozijnenmonteur</strong> bent op zoek naar opdrachten, een fabrikant of installatiebedrijf dat
            vakmensen zoekt, of een particulier die <strong>nieuwe ramen en deuren</strong> of
            <strong> raamkozijnen wil laten monteren</strong> bij een woningrenovatie — je vindt hier snel de juiste match,
            met geverifieerde bedrijven en gratis offertes.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 text-sm">
            <div>
              <h3 className="font-display font-bold text-slate-900 mb-2">Wat we monteren</h3>
              <ul className="text-slate-600 space-y-1">
                <li>Ramen monteren (PVC, aluminium, hout)</li>
                <li>Raamkozijnen &amp; deurkozijnen monteren</li>
                <li>Schuiframen &amp; voordeuren</li>
                <li>Nieuwe ramen en deuren bij renovatie</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display font-bold text-slate-900 mb-2">Voor wie</h3>
              <ul className="text-slate-600 space-y-1">
                <li>Raammonteurs &amp; kozijnenmonteurs</li>
                <li>Fabrikanten &amp; verkopers van ramen en deuren</li>
                <li>Renovatie- en bouwbedrijven</li>
                <li>Particulieren die offertes vergelijken</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display font-bold text-slate-900 mb-2">Merken &amp; materialen</h3>
              <p className="text-slate-600">
                Kömmerling, Schüco, Aliplast, Aluplast, Deceuninck, Veka, Meranti en Aluprof —
                in PVC, aluminium en hout, van goedkope ramen tot kwaliteitsramen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col gap-4 text-slate-500 text-sm">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <span className="font-semibold text-slate-400">Regio's:</span>
            <Link to="/regio/vlaanderen" data-testid="footer-regio-vlaanderen" className="hover:text-orange-400 transition-colors">Ramen monteren in Vlaanderen</Link>
            <Link to="/regio/vlaams-brabant" data-testid="footer-regio-vlaams-brabant" className="hover:text-orange-400 transition-colors">Ramen monteren in Vlaams-Brabant</Link>
          </div>
          <div className="flex items-center justify-between border-t border-slate-800 pt-4">
            <span className="font-display font-bold text-white">Work4you</span>
            <span>© 2026 — Ramen & deuren montage marktplaats</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
